/**
 * YOUTH GLOBAL — BEHAVIOUR
 *
 * The Next.js build drove all of this from React components under
 * `components/motion` and `components/chrome`. Shopify has no component
 * runtime, so each one is re-implemented here against the same data attributes
 * the CSS in yg-theme.css already keys off — [data-reveal], [data-line],
 * [data-mask] and the marquee track.
 *
 * Every behaviour is opt-in through an attribute and no-ops when its markup is
 * absent, so sections can be added and removed in the theme editor freely.
 */
(() => {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* --------------------------------------------------------------- REVEAL
     One observer for every kind of scroll-in. The CSS does the animating; all
     this does is set [data-inview] once, the way the React `useInView` hook did. */
  const revealed = new WeakSet();

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting || revealed.has(entry.target)) continue;
        revealed.add(entry.target);
        entry.target.setAttribute('data-inview', '');
        observer.unobserve(entry.target);
      }
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
  );

  /* ----------------------------------------------------------------- MASK
     A mask cannot observe itself. Chrome factors an element's own clip-path
     into its intersection rect, so a node clipped to zero height reports
     ratio 0 forever and never triggers the reveal that would unclip it — it
     just stays invisible. The React build used a wrapper; here the mask's
     parent is observed and the masks it holds are flagged when it arrives.

     Its own observer, because that parent can be taller than the viewport and
     a ratio threshold on a very tall element may never be met. Being in view
     at all is the signal instead; the per-mask CSS delay keeps the stagger. */
  const masks = new Map();

  const maskObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        for (const node of masks.get(entry.target) ?? []) node.setAttribute('data-inview', '');
        masks.delete(entry.target);
        maskObserver.unobserve(entry.target);
      }
    },
    { rootMargin: '0px 0px -5% 0px', threshold: 0 }
  );

  function watch(root = document) {
    for (const node of root.querySelectorAll('[data-yg-reveal]')) {
      if (!revealed.has(node)) observer.observe(node);
    }

    for (const node of root.querySelectorAll('[data-mask]')) {
      if (node.hasAttribute('data-inview')) continue;

      const trigger = node.parentElement ?? node;
      const group = masks.get(trigger) ?? [];
      if (!group.includes(node)) group.push(node);
      masks.set(trigger, group);
      maskObserver.observe(trigger);
    }
  }

  /* ------------------------------------------------------------- PARALLAX
     Translates a cover layer against the scroll. Driven from one rAF-throttled
     scroll listener rather than one per element. */
  const parallax = [];

  function collectParallax(root = document) {
    for (const node of root.querySelectorAll('[data-yg-parallax]')) {
      if (parallax.some((item) => item.node === node)) continue;
      parallax.push({ node, speed: parseFloat(node.dataset.ygParallax) || 0.15 });
    }
  }

  let parallaxFrame = 0;

  function runParallax() {
    parallaxFrame = 0;
    const viewport = window.innerHeight;

    for (const { node, speed } of parallax) {
      // Measured on the section, which does not move, rather than on the layer,
      // which the last frame already shifted.
      const host = node.parentElement.getBoundingClientRect();
      if (host.bottom < -viewport || host.top > viewport * 2) continue;
      // Measured from the centre so the offset is zero mid-screen, and never
      // past the layer's overscan: a tall section near the edge of the screen
      // used to pull the photograph clear of its own bottom edge.
      const overscan = Math.max(0, (node.offsetHeight - host.height) / 2);
      const raw = (host.top + host.height / 2 - viewport / 2) * -speed;
      const offset = Math.max(-overscan, Math.min(overscan, raw));
      node.style.transform = `translate3d(0, ${offset.toFixed(2)}px, 0)`;
    }
  }

  /* -------------------------------------------------------------- COUNTER
     The final figure is what ships in the HTML, so crawlers and anyone without
     JavaScript read the real number; the count-up only runs once it scrolls in. */
  function startCounter(node) {
    const to = Number(node.dataset.ygCounter);
    const group = node.dataset.group !== 'false';
    const duration = Number(node.dataset.duration) || 1600;
    const format = (value) => (group ? value.toLocaleString('en-IN') : String(value));

    const start = performance.now();
    node.textContent = format(0);

    const tick = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      // Expo-out: fast start, long settle — confident rather than mechanical.
      const eased = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
      node.textContent = format(Math.round(eased * to));
      if (progress < 1) requestAnimationFrame(tick);
    };

    requestAnimationFrame(tick);
  }

  const counterObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        counterObserver.unobserve(entry.target);
        if (!reduced.matches) startCounter(entry.target);
      }
    },
    { threshold: 0.4 }
  );

  /* -------------------------------------------------- AMBIENT BACKGROUND VIDEO
     Plays only while on screen, and never for a visitor who has asked for less
     motion — the poster frame carries the slot instead. */
  const videoObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const video = entry.target;
        if (entry.isIntersecting && !reduced.matches) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      }
    },
    { threshold: 0.15 }
  );

  /* ---------------------------------------------------------------- HEADER */
  function header() {
    const el = document.querySelector('[data-yg-header]');
    if (!el || started.has(el)) return;
    started.add(el);

    const progress = el.querySelector('[data-yg-progress]');
    const megaButton = el.querySelector('[data-yg-mega-button]');
    const mega = el.querySelector('[data-yg-mega]');
    const mobileButton = document.querySelector('[data-yg-mobile-button]');
    const mobile = document.querySelector('[data-yg-mobile]');

    let frame = 0;
    let closeTimer = 0;

    const update = () => {
      frame = 0;
      const y = window.scrollY;
      const max = document.documentElement.scrollHeight - window.innerHeight;
      if (progress) progress.style.transform = `scaleX(${max > 0 ? Math.min(y / max, 1) : 0})`;
      el.toggleAttribute('data-lifted', y > 24);
    };

    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);

    /* ------------------------------------------------------- ecosystem panel */
    const setMega = (open) => {
      if (!mega || !megaButton) return;
      mega.toggleAttribute('data-open', open);
      megaButton.setAttribute('aria-expanded', String(open));
      el.toggleAttribute('data-panel-open', open);
    };

    if (megaButton && mega) {
      const open = () => {
        window.clearTimeout(closeTimer);
        setMega(true);
      };
      const scheduleClose = () => {
        window.clearTimeout(closeTimer);
        closeTimer = window.setTimeout(() => setMega(false), 180);
      };

      megaButton.addEventListener('click', () =>
        setMega(megaButton.getAttribute('aria-expanded') !== 'true')
      );
      megaButton.parentElement.addEventListener('mouseenter', open);
      mega.addEventListener('mouseenter', open);
      el.addEventListener('mouseleave', scheduleClose);

      /* The aside crossfades to whichever vertical is hovered. */
      const panels = mega.querySelectorAll('[data-yg-mega-panel]');
      const register = mega.querySelector('[data-yg-mega-register]');
      const blurb = mega.querySelector('[data-yg-mega-blurb]');

      for (const link of mega.querySelectorAll('[data-yg-mega-link]')) {
        const show = () => {
          const index = link.dataset.index;
          for (const panel of panels) {
            panel.classList.toggle('opacity-0', panel.dataset.ygMegaPanel !== index);
          }
          const active = mega.querySelector(`[data-yg-mega-panel="${index}"]`);
          if (!active) return;
          if (register) {
            register.textContent = active.dataset.register;
            register.style.color = active.dataset.accent;
          }
          if (blurb) blurb.textContent = active.dataset.blurb;
        };
        link.addEventListener('mouseenter', show);
        link.addEventListener('focus', show);
        /* Fills the aside from the first link rather than from markup. The
           panel used to carry a hardcoded copy of one vertical, which went
           stale the moment the menu's contents changed. */
        if (link === mega.querySelector('[data-yg-mega-link]')) show();
      }
    }

    /* --------------------------------------------------------- mobile menu */
    const setMobile = (open) => {
      if (!mobile || !mobileButton) return;
      mobile.toggleAttribute('data-open', open);
      mobileButton.setAttribute('aria-expanded', String(open));
      document.body.style.overflow = open ? 'hidden' : '';
      const label = mobileButton.querySelector('[data-yg-mobile-label]');
      if (label) label.textContent = open ? 'Close' : 'Menu';
    };

    if (mobileButton && mobile) {
      mobileButton.addEventListener('click', () =>
        setMobile(mobileButton.getAttribute('aria-expanded') !== 'true')
      );
    }

    /* -------------------------------------------------------- account menu
       "My profile" opens My submissions and Sign out on click, and closes
       on a click anywhere else. */
    const accountButton = el.querySelector('[data-yg-account-button]');
    const account = el.querySelector('[data-yg-account]');
    const setAccount = (open) => {
      if (!account || !accountButton) return;
      account.hidden = !open;
      accountButton.setAttribute('aria-expanded', String(open));
    };

    if (accountButton && account) {
      accountButton.addEventListener('click', () =>
        setAccount(accountButton.getAttribute('aria-expanded') !== 'true')
      );
      document.addEventListener('click', (event) => {
        if (!accountButton.parentElement.contains(event.target)) setAccount(false);
      });
    }

    window.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      setMega(false);
      setMobile(false);
      setAccount(false);
    });
  }

  /* -------------------------------------------------------------- CAROUSEL
     The hero bed. Crossfades so the banner carries the range of the ecosystem
     rather than one image, and names the IP on screen. */
  function carousel(root) {
    const slides = [...root.querySelectorAll('[data-yg-slide]')];
    /* The caption and frame tabs were taken off the hero; the crossfade stays.
       Both lookups are kept so a section that still carries them works. */
    const tabs = [...root.querySelectorAll('[data-yg-slide-tab]')];
    const label = root.querySelector('[data-yg-slide-label]');
    if (slides.length < 2) return;

    let index = 0;
    let timer = 0;

    const show = (next) => {
      index = (next + slides.length) % slides.length;
      slides.forEach((slide, position) => {
        slide.style.opacity = position === index ? '1' : '0';
        slide.toggleAttribute('aria-hidden', position !== index);
      });
      tabs.forEach((tab, position) => {
        const on = position === index;
        tab.setAttribute('aria-selected', String(on));
        tab.style.background = on ? tab.dataset.tone : '';
      });
      if (label) label.textContent = slides[index].dataset.label || '';
    };

    const play = () => {
      if (reduced.matches) return;
      window.clearInterval(timer);
      timer = window.setInterval(() => show(index + 1), 4600);
    };

    tabs.forEach((tab, position) =>
      tab.addEventListener('click', () => {
        show(position);
        play();
      })
    );

    show(0);
    play();
  }

  /* ---------------------------------------------------------------- SOUND
     A film with a soundtrack starts muted, since browsers only allow sound
     after a click. The button is that click: it unmutes, and plays the film
     if reduced motion had kept it still. */
  function sound(button) {
    const video = button.closest('[data-yg-sound-host]')?.querySelector('video');
    if (!video) return;
    button.addEventListener('click', () => {
      video.muted = !video.muted;
      if (!video.muted) video.play().catch(() => {});
      button.setAttribute('aria-pressed', String(!video.muted));
    });
  }

  /* ---------------------------------------------------------- TRACK STUDIO
     One decision, one form. The rows are a tablist, so arrow keys move between
     them — what a keyboard user expects of a row of choices like this. */
  function trackStudio(root) {
    const tabs = [...root.querySelectorAll('[role="tab"]')];
    const panels = [...root.querySelectorAll('[data-yg-track-panel]')];
    if (!tabs.length) return;

    const select = (next, focus) => {
      tabs.forEach((tab, position) => {
        const on = position === next;
        tab.setAttribute('aria-selected', String(on));
        tab.tabIndex = on ? 0 : -1;
        if (on && focus) tab.focus();
      });
      panels.forEach((panel, position) => panel.toggleAttribute('hidden', position !== next));
    };

    tabs.forEach((tab, position) => {
      tab.addEventListener('click', () => select(position, false));
      tab.addEventListener('keydown', (event) => {
        const step =
          event.key === 'ArrowDown' || event.key === 'ArrowRight'
            ? 1
            : event.key === 'ArrowUp' || event.key === 'ArrowLeft'
              ? -1
              : 0;
        if (!step) return;
        event.preventDefault();
        select((position + step + tabs.length) % tabs.length, true);
      });
    });

    /* A link can name a track: #choose=originals-filmmaker. That is how the
       hero's three buttons work, and why the selector can be hidden without
       the page losing the ability to open on the right one. */
    const fromHash = () => {
      const match = /[#&]choose=([\w-]+)/.exec(window.location.hash);
      if (!match) return -1;
      return tabs.findIndex((tab) => tab.dataset.ygTrackKey === match[1]);
    };

    const openFromHash = (scroll) => {
      const found = fromHash();
      if (found === -1) return false;
      select(found, false);
      if (scroll) root.closest('section')?.scrollIntoView({ behavior: reduced.matches ? 'auto' : 'smooth', block: 'start' });
      return true;
    };

    window.addEventListener('hashchange', () => openFromHash(true));

    /* Back from a send (?yg-sent=<form id>): the tab holding that form opens,
       or its "received" panel would sit inside a hidden tab. */
    const sent = new URLSearchParams(window.location.search).get('yg-sent');
    const sentTab = sent ? panels.findIndex((panel) => panel.querySelector(`[data-yg-form="${sent}"]`)) : -1;

    if (!openFromHash(false)) select(sentTab === -1 ? 0 : sentTab, false);
  }

  /* ---------------------------------------------------------------- VIDEO
     A facade rather than an embed: YouTube's player, and its trackers, only
     load once someone actually asks for the video. */
  function video(root) {
    const button = root.querySelector('[data-yg-video-play]');
    if (!button) return;

    button.addEventListener('click', () => {
      const frame = document.createElement('iframe');
      frame.src = `https://www.youtube-nocookie.com/embed/${root.dataset.ygVideo}?autoplay=1&rel=0`;
      frame.title = button.textContent.trim();
      frame.allow = 'accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture';
      frame.allowFullscreen = true;
      frame.className = 'absolute inset-0 h-full w-full';
      frame.setAttribute('frameborder', '0');
      root.replaceChildren(frame);
    });
  }

  /* ----------------------------------------------------------- SWITCHBOARD
     The nine label services. The grid drives one detail panel, so the section
     reads as a single ecosystem rather than nine boxes. */
  function switchboard(root) {
    const cells = [...root.querySelectorAll('[data-yg-service]')];
    const title = root.querySelector('[data-yg-service-title]');
    const copy = root.querySelector('[data-yg-service-copy]');
    const note = root.querySelector('[data-yg-service-note]');
    if (!cells.length || !title || !copy) return;

    const show = (cell) => {
      for (const other of cells) other.setAttribute('aria-pressed', String(other === cell));
      title.textContent = `${cell.dataset.number}, ${cell.dataset.name}`;
      copy.textContent = cell.dataset.copy;

      // One service says exactly what the standing note says. Printing the
      // same sentence twice, one above the other, reads as a mistake.
      if (note) {
        note.hidden = (note.dataset.text || '').trim() === (cell.dataset.copy || '').trim();
      }

      // Re-triggers the fade so the swap reads as a change, not a jump.
      copy.style.animation = 'none';
      void copy.offsetWidth;
      copy.style.animation = '';
    };

    for (const cell of cells) {
      cell.addEventListener('mouseenter', () => show(cell));
      cell.addEventListener('focus', () => show(cell));
      cell.addEventListener('click', () => show(cell));
    }

    const open = cells.find((cell) => cell.getAttribute('aria-pressed') === 'true');
    if (open) show(open);
  }

  /* ------------------------------------------------------------ SUBMISSIONS
     A form never leaves the page. Its copy goes to the team's Sheet (which
     mails the sender a confirmation), and Shopify's contact post, which sends
     the store its "New customer message" email, goes into a hidden frame
     instead of reloading the page. Then the "received" panel opens as a popup
     where the sender is, with the Pay button on a paid form; on a YOUth
     Originals form the guidelines come first, and Pay shows once they are
     ticked (user, 2026-10-31: no jumping back up, SOP in a popup).

     Without JavaScript the form posts normally and comes back to
     ?yg-sent=<form id>, which opens the same panel. */
  function submissions() {
    const sent = new URLSearchParams(window.location.search).get('yg-sent');

    for (const form of document.querySelectorAll('[data-yg-form]')) {
      /* The form inside the fields, not the first form in the box: on a paid
         form the success panel comes first and holds the Pay button's own
         cart form (2026-10-31). */
      const element = form.querySelector('[data-yg-fields] form');
      const success = form.querySelector('[data-yg-success]');

      /* The guidelines tick box gates the Pay button. */
      const agree = success?.querySelector('[data-yg-sop-agree]');
      const pay = success?.querySelector('[data-yg-pay]');
      const wait = success?.querySelector('[data-yg-pay-wait]');
      if (agree && pay) {
        const gate = () => {
          pay.hidden = !agree.checked;
          if (wait) wait.hidden = agree.checked;
        };
        agree.addEventListener('change', () => {
          gate();
          /* Pay opens below the fold of the popup, so ticking looked like it
             did nothing and people left without paying (2026-11-03). Bring
             it into view. */
          if (agree.checked) pay.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        });
        gate();
      }

      /* The popup. The panel moves into it and back, so there is only ever
         one copy of the Pay form and the tick box. */
      let layer = null;
      const home = success ? document.createComment('yg-success') : null;
      if (success) success.before(home);

      const close = () => {
        if (!layer) return;
        /* The form gives way to the panel only now, so the page behind the
           popup never shifts while it is open. */
        form.querySelector('[data-yg-fields]')?.setAttribute('hidden', '');
        home.after(success);
        layer.remove();
        layer = null;
        document.body.classList.remove('yg-done-open');
        /* Back on the page the form has gone and the panel stands in its
           place, so the page lands on it rather than on whatever moved up. */
        success.scrollIntoView({ block: 'center', behavior: 'auto' });
      };

      const reveal = () => {
        if (!success || layer) return;
        success.hidden = false;

        layer = document.createElement('div');
        layer.className = 'yg-done';
        layer.setAttribute('role', 'dialog');
        layer.setAttribute('aria-modal', 'true');
        layer.setAttribute('aria-label', form.dataset.ygTitle || 'Submission received');
        layer.style.setProperty('--accent', form.style.getPropertyValue('--accent'));
        const panel = document.createElement('div');
        panel.className = 'yg-done__panel';
        panel.append(success);
        layer.append(panel);
        document.body.append(layer);
        document.body.classList.add('yg-done-open');

        layer.addEventListener('click', (event) => {
          if (event.target === layer || event.target.closest('[data-yg-done-close]')) close();
        });
        (agree || success.querySelector('[data-yg-pay] [type="submit"]') || success.querySelector('[data-yg-done-close]'))?.focus({ preventScroll: true });
      };

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') close();
      });

      if (element) {
        /* When this form came into view, so the script can tell a person
           filling it in from a script posting to it. Taken at page load
           rather than at focus: a form can be submitted without ever being
           focused, and a missing number is treated as a pass. */
        const renderedAt = Date.now();

        /* A link typed as "drive.google.com/…" or "www.…" is what people
           paste, and a url field refuses it with "Please enter a URL" before
           the form is even sent. The missing https:// is added instead. This
           has to run before the browser checks the field, so on change, on
           the send button and on Enter, not in the submit handler. */
        const fixLinks = () => {
          for (const input of element.querySelectorAll('input[type="url"]')) {
            const value = input.value.trim();
            if (value && !/^[a-z][a-z0-9+.-]*:/i.test(value)) {
              input.value = 'https://' + value.replace(/^\/+/, '');
            } else if (value !== input.value) {
              input.value = value;
            }
          }
        };
        element.addEventListener('change', (event) => {
          if (event.target.matches?.('input[type="url"]')) fixLinks();
        });
        element.addEventListener('click', (event) => {
          if (event.target.closest?.('[type="submit"]')) fixLinks();
        }, true);
        element.addEventListener('keydown', (event) => {
          if (event.key === 'Enter') fixLinks();
        }, true);

        /* Shopify's post lands here instead of reloading the page. */
        const frameName = 'yg-post-' + form.dataset.ygForm;
        let frame = null;

        const button = element.querySelector('[type="submit"]');
        const label = button?.querySelector('span.relative');
        const idle = label?.textContent || '';
        let sending = false;

        const problem = (show) => {
          let note = element.querySelector('[data-yg-send-problem]');
          if (!note && show) {
            note = document.createElement('p');
            note.dataset.ygSendProblem = '';
            note.setAttribute('role', 'alert');
            note.className = 'mt-6 border-l-2 border-ember pl-4 text-sm text-ember';
            note.textContent = 'That did not go through. Check your connection and press send again.';
            element.append(note);
          }
          if (note) note.hidden = !show;
        };

        element.addEventListener('submit', (event) => {
          /* Shopify's spam check (hCaptcha) listens after this and sends the
             form itself whether or not it was stopped, so a stop here has to
             stop the other listeners too. */
          const stop = () => {
            event.preventDefault();
            event.stopImmediatePropagation();
          };

          // The honeypot is unprefixed, so Shopify drops it either way; this
          // stops the post before it becomes a notification email.
          if (element.querySelector('[name="yg_hp"]')?.value) {
            stop();
            return;
          }

          // A chip group is a set of checkboxes, which the browser can only
          // require one at a time — so "pick at least one" is enforced here.
          for (const group of element.querySelectorAll('[data-yg-chips-required]')) {
            if (group.querySelector('input:checked')) {
              group.removeAttribute('data-yg-invalid');
              continue;
            }
            stop();
            group.setAttribute('data-yg-invalid', '');
            group.scrollIntoView({ block: 'center', behavior: reduced.matches ? 'auto' : 'smooth' });
            return;
          }

          // A second press while the first is on its way.
          if (sending) {
            stop();
            return;
          }

          const endpoint = document.documentElement.dataset.ygSubmissions;
          if (!endpoint || !window.fetch) return; // the plain post, as without JavaScript

          sending = true;
          problem(false);
          if (button) button.disabled = true;
          if (label) label.textContent = 'Sending…';

          /* Shopify's post (and its spam check) carry on as usual, only into
             the hidden frame. */
          if (!frame) {
            frame = document.createElement('iframe');
            frame.name = frameName;
            frame.title = 'Sending';
            frame.hidden = true;
            frame.setAttribute('aria-hidden', 'true');
            document.body.append(frame);
          }
          element.target = frameName;
          const shopify = new Promise((resolve) => {
            frame.addEventListener('load', () => {
              let landed = false;
              try { landed = /yg-sent=/.test(frame.contentWindow.location.search); } catch { landed = false; }
              resolve(landed);
            }, { once: true });
            setTimeout(() => resolve(false), 20000);
          });

          const answers = {};
          for (const [key, value] of new FormData(element).entries()) {
            const match = key.match(/^contact\[(.+)\]$/);
            if (match && String(value).trim()) answers[match[1]] = value;
          }

          const sheet = fetch(endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'text/plain;charset=utf-8' },
            keepalive: true,
            body: JSON.stringify({
              form: form.dataset.ygForm,
              track: form.dataset.ygTitle || '',
              fee: form.dataset.ygFee || '',
              page: window.location.pathname,
              company: element.querySelector('[name="yg_hp"]')?.value || '',
              elapsedMs: Date.now() - renderedAt,
              answers,
            }),
          })
            .then((response) => response.json())
            .then((result) => Boolean(result && result.ok))
            .catch(() => false);

          /* The panel opens as soon as either copy is safe. Neither, and the
             sender is told to try again with everything still filled in. */
          let done = false;
          const finish = (ok) => {
            if (done) return;
            if (!ok) return;
            done = true;
            if (label) label.textContent = idle;
            reveal();
          };
          sheet.then(finish);
          shopify.then(finish);
          Promise.all([sheet, shopify]).then(([a, b]) => {
            if (a || b || done) return;
            sending = false;
            if (button) button.disabled = false;
            if (label) label.textContent = idle;
            problem(true);
          });
        });
      }

      if (!success) continue;

      if (form.dataset.ygForm === sent) reveal();
      else success.hidden = true;
    }
  }

  /* ----------------------------------------------------------------- FILTER
     The events browser. Pure show/hide over rendered cards — no fetch, so it
     stays instant and keeps working from a bookmarked filter. */
  function filters(root) {
    const buttons = [...root.querySelectorAll('[data-yg-filter]')];
    const items = [...root.querySelectorAll('[data-yg-filter-item]')];
    const empty = root.querySelector('[data-yg-filter-empty]');

    const apply = (value) => {
      let shown = 0;
      for (const item of items) {
        const on = value === 'all' || (item.dataset.ygFilterItem || '').split(' ').includes(value);
        item.hidden = !on;
        if (on) shown += 1;
      }
      for (const button of buttons) {
        button.toggleAttribute('data-active', button.dataset.ygFilter === value);
      }
      if (empty) empty.hidden = shown > 0;
    };

    for (const button of buttons) {
      button.addEventListener('click', () => apply(button.dataset.ygFilter));
    }

    apply('all');
  }

  /* --------------------------------------------------------------- ROUTES
     A route picker shows one of its sections at a time, the first before a
     pick. A card, or any link to a section's anchor (the hero's buttons), picks
     it; so does #choose=<form id>, for a form inside one of those sections.
     The hiding happens here, so without JavaScript every form stays visible. */
  function routes(picker) {
    const group = picker.dataset.ygRoutes;
    const panels = [...document.querySelectorAll('[data-yg-route-panel]')].filter(
      (panel) => panel.dataset.ygRoutePanel === group
    );
    const cards = [...picker.querySelectorAll('[data-yg-route]')];
    if (!panels.length) return;

    const show = (id, scroll) => {
      const target = panels.find((panel) => panel.id === id);
      if (!target) return false;
      for (const panel of panels) panel.hidden = panel !== target;
      for (const card of cards) card.setAttribute('aria-pressed', String(card.dataset.ygRoute === id));
      if (scroll) target.scrollIntoView({ behavior: reduced.matches ? 'auto' : 'smooth', block: 'start' });
      return true;
    };

    const fromHash = () => {
      const hash = decodeURIComponent(window.location.hash.slice(1));
      if (!hash) return null;
      const chosen = /^choose=([\w-]+)/.exec(hash);
      if (chosen) {
        const owner = panels.find((panel) => panel.querySelector(`[data-yg-track-key="${chosen[1]}"]`));
        return owner ? owner.id : null;
      }
      return panels.some((panel) => panel.id === hash) ? hash : null;
    };

    /* Any link to one of the panels opens it: the hero's buttons, or links
       elsewhere on the page. Handled on click as well as on hashchange, so a
       second press of the same button still scrolls to its form. */
    document.addEventListener('click', (event) => {
      const link = event.target.closest('a[href^="#"]');
      if (!link || link.hasAttribute('data-yg-route')) return;
      const id = decodeURIComponent(link.getAttribute('href').slice(1));
      if (!panels.some((panel) => panel.id === id)) return;
      event.preventDefault();
      show(id, true);
      try {
        history.replaceState(null, '', '#' + id);
      } catch (error) {
        /* The form is open either way. */
      }
    });

    for (const card of cards) {
      card.addEventListener('click', (event) => {
        event.preventDefault();
        show(card.dataset.ygRoute, true);
        // Keeps the pick in the address so a shared link opens the same form.
        try {
          history.replaceState(null, '', '#' + card.dataset.ygRoute);
        } catch (error) {
          /* Some contexts refuse it; the form is already open either way. */
        }
      });
    }

    window.addEventListener('hashchange', () => {
      const id = fromHash();
      if (id) show(id, true);
    });

    /* With no pick in the address the first section opens, the one behind
       the hero's first, solid button (user, 2026-10-27). Only a pick in the
       address scrolls to it. */
    const initial = fromHash();

    /* Back from a send (?yg-sent=<form id>), the section holding that form
       opens, not the first one; otherwise a Filmmaker or Student sender
       lands on the Championship and never sees their "received" panel
       (2026-10-31). submissions() runs after this and scrolls to it. */
    const sent = new URLSearchParams(window.location.search).get('yg-sent');
    const sentPanel = sent ? panels.find((panel) => panel.querySelector(`[data-yg-form="${sent}"]`)) : null;

    show(initial || sentPanel?.id || panels[0].id, false);
    if (initial) {
      requestAnimationFrame(() => document.getElementById(initial)?.scrollIntoView({ block: 'start' }));
    }
  }

  /* ------------------------------------------------------------- LIGHTBOX
     The Gallery viewer. Opens on whichever frame was clicked, walks through
     the ones the current filter shows, and copies the picture or film into a
     native dialog. A film plays there with its controls and sound; closing
     empties the dialog, which stops it. */
  function lightbox(root) {
    const dialog = root.querySelector('[data-yg-lightbox-dialog]');
    if (!dialog || typeof dialog.showModal !== 'function') return;
    const stage = dialog.querySelector('[data-yg-lightbox-stage]');
    const caption = dialog.querySelector('[data-yg-lightbox-caption]');
    const visible = () =>
      [...root.querySelectorAll('[data-yg-lightbox-item]')].filter((item) => !item.closest('[hidden]'));
    let current = -1;

    const show = (index) => {
      const list = visible();
      if (!list.length) return;
      current = (index + list.length) % list.length;
      const item = list[current];
      const film = item.querySelector('video');
      const picture = item.querySelector('img');
      stage.replaceChildren();
      if (film) {
        const video = document.createElement('video');
        video.src = film.querySelector('source')?.src || film.currentSrc;
        if (film.poster) video.poster = film.poster;
        video.controls = true;
        video.playsInline = true;
        video.autoplay = true;
        stage.append(video);
      } else if (picture) {
        const image = document.createElement('img');
        image.src = picture.currentSrc || picture.src;
        image.alt = picture.alt;
        stage.append(image);
      }
      caption.textContent = item.dataset.caption || '';
      if (!dialog.open) dialog.showModal();
    };

    root.addEventListener('click', (event) => {
      const item = event.target.closest('[data-yg-lightbox-item]');
      if (item && !dialog.contains(item)) show(visible().indexOf(item));
    });
    dialog.querySelector('[data-yg-lightbox-prev]')?.addEventListener('click', () => show(current - 1));
    dialog.querySelector('[data-yg-lightbox-next]')?.addEventListener('click', () => show(current + 1));
    dialog.querySelector('[data-yg-lightbox-close]')?.addEventListener('click', () => dialog.close());
    dialog.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') show(current + 1);
      if (event.key === 'ArrowLeft') show(current - 1);
    });
    // A click on the dark space around the frame closes it, as on most viewers.
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog || event.target === stage) dialog.close();
    });
    dialog.addEventListener('close', () => stage.replaceChildren());
  }

  /* -------------------------------------------------------------- BOOTSTRAP */
  /* The theme editor fires section:load and section:select on every change, so
     init runs repeatedly over markup that is often the same. Without this each
     pass would add another timer and another set of listeners: two carousels on
     one element keep separate counters, and the slide ends up showing one frame
     while the caption names another. */
  const started = new WeakSet();

  function once(node, start) {
    if (started.has(node)) return;
    started.add(node);
    start(node);
  }

  function init(root = document) {
    watch(root);
    collectParallax(root);

    for (const node of root.querySelectorAll('[data-yg-counter]')) counterObserver.observe(node);
    for (const node of root.querySelectorAll('[data-yg-ambient]')) videoObserver.observe(node);
    for (const node of root.querySelectorAll('[data-yg-carousel]')) once(node, carousel);
    for (const node of root.querySelectorAll('[data-yg-track-studio]')) once(node, trackStudio);
    for (const node of root.querySelectorAll('[data-yg-filters]')) once(node, filters);
    for (const node of root.querySelectorAll('[data-yg-switchboard]')) once(node, switchboard);
    for (const node of root.querySelectorAll('[data-yg-video]')) once(node, video);
    for (const node of root.querySelectorAll('[data-yg-sound]')) once(node, sound);
    for (const node of root.querySelectorAll('[data-yg-lightbox]')) once(node, lightbox);
    for (const node of root.querySelectorAll('[data-yg-routes]')) once(node, routes);

    if (parallax.length) {
      window.addEventListener(
        'scroll',
        () => {
          if (!parallaxFrame && !reduced.matches) parallaxFrame = requestAnimationFrame(runParallax);
        },
        { passive: true }
      );
      runParallax();
    }
  }

  header();
  init();
  submissions();

  /* The theme editor swaps section markup in and out without a reload, so every
     behaviour is re-bound against the section that just changed. */
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:select', (event) => init(event.target));
})();
